import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { pb } from '@/core/api';
import { useActivePricing, type Tier } from '@/hooks/use-pricing';
import {
  useCollectorSummary,
  useDailyEarnings,
} from './use-collector-earnings';
import { usePaymentHistory } from './use-payment-history';
import {
  getBaseEarnings,
  getAvgLeadsPerDay,
  getCurrentTier,
} from '../utils/earnings-calculations';

import type { RankingEntry } from '../utils/bonus-indicators';

type RE = RankingEntry;
type Derived = {
  avgPerDay: number;
  currentTier: Tier | undefined;
  baseEarnings: number;
  rankPosition: number | null;
  creatorsToNext: number | null;
  periodCreators: number;
};

const ACTIVITY_WINDOW_DAYS = 180;

function getActivityRange(): { from: string; to: string } {
  const iso = (d: Date): string => d.toISOString().slice(0, 10);
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (ACTIVITY_WINDOW_DAYS - 1));
  return { from: iso(from), to: iso(to) };
}

function useRanking() {
  return useQuery({
    queryKey: ['earnings-ranking'],
    queryFn: async (): Promise<RE[]> =>
      pb.send('/api/earnings/ranking', { method: 'GET' }),
    staleTime: 30_000,
  });
}

function rankInfo(
  r: RE[] | undefined,
  uid: string | undefined
): { rankPosition: number | null; creatorsToNext: number | null } {
  if (!r || !uid) return { rankPosition: null, creatorsToNext: null };
  const i = r.findIndex((e) => e.collector_id === uid);
  if (i === -1) return { rankPosition: null, creatorsToNext: null };
  const prev = r[i - 1];
  return {
    rankPosition: r[i]!.position,
    creatorsToNext:
      i > 0 && prev ? prev.total_leads - r[i]!.total_leads + 1 : null,
  };
}

type DeriveInput = {
  summary: ReturnType<typeof useCollectorSummary>;
  daily: ReturnType<typeof useDailyEarnings>;
  pricing: ReturnType<typeof useActivePricing>;
  ranking: ReturnType<typeof useRanking>;
  userId: string | undefined;
};

function useDerived({
  summary,
  daily,
  pricing,
  ranking,
  userId,
}: DeriveInput): Derived | null {
  return useMemo((): Derived | null => {
    if (!summary.data || !pricing.data) return null;
    const d = daily.data ?? [];
    const avg = getAvgLeadsPerDay(d);
    return {
      avgPerDay: avg,
      currentTier: getCurrentTier(avg, pricing.data.tiers),
      baseEarnings: getBaseEarnings(summary.data),
      ...rankInfo(ranking.data, userId),
      periodCreators: d.reduce((s, e) => s + e.lead_count, 0),
    };
  }, [summary.data, daily.data, pricing.data, ranking.data, userId]);
}

export function useEarningsPageData(userId: string | undefined) {
  const summary = useCollectorSummary(userId);
  const { from, to } = getActivityRange();
  const daily = useDailyEarnings(userId ?? '', from, to);
  const history = usePaymentHistory();
  const pricing = useActivePricing();
  const ranking = useRanking();
  const derived = useDerived({ summary, daily, pricing, ranking, userId });
  return {
    summary,
    daily,
    history,
    pricing,
    derived,
    ranking,
    isLoading: summary.isLoading,
  };
}
